import { DATE_FORMAT } from './i18n/date-formats';

import type {
  HighlightHotels,
  PriceType,
  SortType,
  TravellerContext,
} from '@shared/types/frontend/ubff/common';
import type { I18nService } from '@shared/types/i18n';
import type { UserContext } from '@shared/types/user-context';
import type {
  Maybe,
  Optional,
  Stay,
  StaySerialized,
} from '@shared/types/utils';

export const buildTravellerContext = (
  i18n: I18nService,
  stay: Stay | StaySerialized,
): TravellerContext => {
  const { currency, locale, market } = i18n.culture;
  const { checkIn, checkOut, childrenAges, numberOfAdults, numberOfRooms } =
    stay;

  return {
    market,
    locale,
    currency,
    checkinDate: i18n.formatDate(checkIn, DATE_FORMAT.NON_LOCALISED_SHORT),
    checkoutDate: i18n.formatDate(checkOut, DATE_FORMAT.NON_LOCALISED_SHORT),
    adults: numberOfAdults,
    childrenAges,
    rooms: numberOfRooms,
  };
};

export const buildHopsVersion = (userContext: UserContext) => {
  const {
    hopsVersion,
    hpaHopsVersion,
    partnerSortingEnabled,
    partnerSortingHpaEnabled,
  } = userContext;

  if (partnerSortingEnabled) {
    return hopsVersion;
  }

  if (partnerSortingHpaEnabled) {
    return hpaHopsVersion;
  }

  return undefined;
};

export const buildPartnerSortingContext = ({
  i18n,
  includeSearchTimeOfDay = true,
  userContext,
}: {
  userContext: UserContext;
  i18n: I18nService;
  includeSearchTimeOfDay?: boolean;
}) => {
  const context: {
    sortedPartner?: string;
    defaultDate: boolean;
    searchTimeOfDay?: string;
  } = {
    sortedPartner: buildHopsVersion(userContext),
    defaultDate: false,
  };

  if (includeSearchTimeOfDay) {
    context.searchTimeOfDay = i18n.formatDate(
      new Date(),
      DATE_FORMAT.NON_LOCALISED_FULL,
    );
  }

  return context;
};

export const buildHighlightHotels = ({
  overrideHotelSortScore,
  upSortHotels,
}: {
  overrideHotelSortScore: Optional<string>;
  upSortHotels: Maybe<string>;
}): Optional<HighlightHotels> => {
  if (overrideHotelSortScore) {
    return {
      hotelIds: [overrideHotelSortScore],
      highlightType: 'HIGHLIGHT_TYPE_OVERRIDE_SORT_SCORE',
    };
  }

  if (upSortHotels) {
    return {
      hotelIds: upSortHotels.split(','),
      highlightType: 'HIGHLIGHT_TYPE_UPSORT',
    };
  }

  return undefined;
};

export const buildSearchParams = ({
  entityId,
  filters,
  i18n,
  includeSearchTimeOfDay = true,
  offset,
  overrideHotelSortScore,
  priceType,
  searchId,
  sessionToken,
  sortType,
  stay,
  traceInfo,
  upSortHotels,
  userContext,
}: {
  entityId: string;
  filters: any;
  i18n: I18nService;
  includeSearchTimeOfDay?: boolean;
  offset: number;
  overrideHotelSortScore?: Optional<string>;
  priceType: PriceType;
  searchId?: Optional<string>;
  sessionToken?: Optional<string>;
  sortType: SortType;
  stay: Stay | StaySerialized;
  traceInfo: Maybe<string>;
  upSortHotels: Maybe<string>;
  userContext: UserContext;
}) => ({
  priceType,
  entityId,
  partnerSortingContext: buildPartnerSortingContext({
    userContext,
    i18n,
    includeSearchTimeOfDay,
  }),
  requestContext: { debug: userContext.showDebugInfo },
  travellerContext: buildTravellerContext(i18n, stay),
  offset,
  limit: userContext.hotelCardListCount,
  sortType,
  cugSegments: userContext.cugOverride,
  highlightHotels: buildHighlightHotels({
    overrideHotelSortScore,
    upSortHotels,
  }),
  traceInfo: traceInfo ?? undefined,
  partnersPerHotel: -1,
  imagesPerHotel: 5,
  offersPerPartner: 1,
  filters,
  searchId,
  sessionToken,
});
